/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useState } from 'react'

import { Box, CircularProgress, useTheme, Typography } from '@mui/material'
import { MapLayer, MapLayerType } from '@app/redux/riskMap/riskMapSlice'
import { RootState } from '@app/redux/store'
import { Button } from '@app/components/Atoms/Button'
import { useSelector } from 'react-redux'
import {
  addLayersDialogContainer,
  addLayersDialogTitle,
  addLayersFooter,
  loadingSection,
} from './AddLayersDialog.styles'
import { LayerSetup } from './LayerSetup'
import { addLayersSetupContainer } from './LayerSetup/LayerSetup.styles'
import { LayersPreview } from './LayersPreview'
import { css } from '@emotion/react'
import { useNavigate } from 'react-router-dom'
import { appLink } from '@app/config/appLink'
import { useLayers } from '@app/contexts/LayerContext'
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext'
import { ModalLayer } from '@app/components/Molecules/LayersControl'
import { useLayerDataFetcher } from '@app/components/Molecules/RiskSideDrawerContent/data_fetchers/layerDataFetcher'
import { HazardOptionStateByHazard } from '@app/components/Molecules/RiskSideDrawerContent/tabs/InformativePanel/LayersTab/HazardControls'
import { logDataDogAction } from '@app/utils/logger'
import { DATADOG_ACTIONS } from '@app/app-constants'
import { useLiveContext } from '@app/modules/Live/contexts/Live.Context'
import { LiveLayer } from '@app/modules/Live/Live.types'

export interface AddLayersDialogProps {
  onClose: () => void
  liveData?: boolean
}

export const AddLayersDialog = ({ onClose, liveData }: AddLayersDialogProps) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { setHazardOptionState } = useMap()
  const { prepareAndUpdateLayers } = useLayers()
  const { setActiveLiveLayers, liveMapSelected } = useLiveContext()
  const { layers } = useSelector((state: RootState) => state.riskMap)

  const mapLayerToModalLayerShape = (layers: MapLayer[]) => {
    const reducedLayersByType = layers.reduce(
      (acc, layer) => {
        const layerType = layer.layerType
        return {
          ...acc,
          [layerType]: [...(acc[layerType] || []), layer],
        }
      },
      {} as Record<MapLayerType, MapLayer[]>,
    )

    return Object.keys(reducedLayersByType).map((layerType) => {
      return {
        id: layerType as MapLayerType,
        layers: reducedLayersByType[layerType as MapLayerType],
      }
    })
  }

  const [stagedLayers, setStagedLayers] = useState<ModalLayer[]>(mapLayerToModalLayerShape(layers))

  const [selectedHazards, setSelectedHazards] = useState<MapLayer[]>(
    stagedLayers.find((layer) => layer.id === 'hazard')?.layers || [],
  )
  const [selectedAssets, setSelectedAssets] = useState<MapLayer[]>(
    stagedLayers.find((layer) => layer.id === 'asset')?.layers || [],
  )
  const [selectedInformation, setSelectedInformation] = useState<MapLayer[]>(
    stagedLayers.find((layer) => layer.id === 'information')?.layers || [],
  )
  const [selectedLive, setSelectedLive] = useState<LiveLayer[]>((): LiveLayer[] => {
    const liveStaged: LiveLayer[] =
      (stagedLayers.find((layer) => layer.id === 'live')?.layers as LiveLayer[]) || []
    return liveStaged
  })

  const dialogData = useLayerDataFetcher()

  const handleUpdateLayers = (newLayers: MapLayer[], type?: MapLayerType) => {
    // if there are no layers, clear the selected layers
    if (newLayers.length === 0) {
      if (selectedHazards.length > 0) setSelectedHazards([])
      if (selectedAssets.length > 0) setSelectedAssets([])
      if (selectedInformation.length > 0) setSelectedInformation([])
      if (selectedLive.length > 0) setSelectedLive([])
    }
    if (!type) {
      // group each layer in newLayers by type
      const layersByType = newLayers.reduce(
        (acc, layer) => {
          const layerType = layer.layerType
          return {
            ...acc,
            [layerType]: [...(acc[layerType] || []), layer],
          }
        },
        {} as Record<MapLayerType, MapLayer[]>,
      )
      // for each layer type, update the selected layers
      const types = ['hazard', 'asset', 'information']

      for (const type of types) {
        const layers = layersByType[type as MapLayerType] || []
        if (type === 'hazard') setSelectedHazards(layers)
        if (type === 'asset') setSelectedAssets(layers)
        if (type === 'information') setSelectedInformation(layers)
        if (type === 'live') setSelectedLive(layers as LiveLayer[])
      }

      const updatedNewLayers = mapLayerToModalLayerShape(newLayers)
      setStagedLayers(updatedNewLayers)
      return
    }

    const isExistingLayerGroup = stagedLayers.some((layer) => layer.id === type)

    let updatedLayers = [...stagedLayers]

    if (isExistingLayerGroup) {
      updatedLayers = updatedLayers.map((layer) => {
        if (layer.id === type) {
          return {
            ...layer,
            layers: newLayers,
          }
        }
        return { ...layer }
      })
    } else {
      updatedLayers.push({
        id: type,
        layers: newLayers,
      })
    }

    /* If there is no asset layer group, add the group as the first element in the array */
    const hasAssetGroupsInCurrentLayers = stagedLayers.some((layer) => layer.id === 'asset')
    if (!hasAssetGroupsInCurrentLayers) {
      const assetLayerGroup = updatedLayers.find((layer) => layer.id === 'asset')
      const remainingLayers = updatedLayers.filter((layer) => layer.id !== 'asset')

      if (assetLayerGroup) {
        updatedLayers = [assetLayerGroup, ...remainingLayers]
      }
    }

    setStagedLayers(updatedLayers)
  }

  const handleSelectHazardLayer = (selectedHazard: MapLayer) => {
    if (!selectedHazard) return

    logDataDogAction(DATADOG_ACTIONS.ADD_LAYER, {
      id: selectedHazard.assetTag,
      type: 'hazard',
    })

    const existingHazardLayers = stagedLayers.find((layer) => layer.id === 'hazard')?.layers || []

    const updatedHazards = [...existingHazardLayers, selectedHazard]
    setSelectedHazards(updatedHazards)
    handleUpdateLayers(updatedHazards, 'hazard')
  }

  const handleSelectAssetLayer = (newAssets: MapLayer[]) => {
    logDataDogAction(DATADOG_ACTIONS.ADD_LAYER, {
      id: newAssets[0].type,
      type: 'asset',
    })

    const existingAssetLayers = stagedLayers.find((layer) => layer.id === 'asset')?.layers || []
    const updatedAssets = [...existingAssetLayers, ...newAssets]
    setSelectedAssets(updatedAssets)
    handleUpdateLayers(updatedAssets, 'asset')
  }

  const handleSelectInformationLayer = (selectedInformationLayer: MapLayer) => {
    logDataDogAction(DATADOG_ACTIONS.ADD_LAYER, {
      id: selectedInformationLayer.id,
      type: 'information',
    })

    const existingInformationLayers =
      stagedLayers.find((layer) => layer.id === 'information')?.layers || []
    const updatedInfoLayers = [...existingInformationLayers, selectedInformationLayer]
    setSelectedInformation(updatedInfoLayers)
    handleUpdateLayers(updatedInfoLayers, 'information')
  }

  const handleSelectLiveLayer = (selectedLiveLayer: LiveLayer) => {
    logDataDogAction(DATADOG_ACTIONS.ADD_LAYER, {
      id: selectedLiveLayer.id,
      type: 'live',
    })

    const existingLiveLayers: LiveLayer[] =
      (stagedLayers.find((layer) => layer.id === 'live')?.layers as LiveLayer[]) || []
    const updatedLiveLayers: LiveLayer[] = [...existingLiveLayers, selectedLiveLayer]

    setSelectedLive(updatedLiveLayers)
    handleUpdateLayers(updatedLiveLayers, 'live')
  }

  const handleToggleLayerVisibility = (selectedLayer: MapLayer) => {
    const layerType = selectedLayer.layerType

    const layerGroup = stagedLayers.find((layer) => layer.id === layerType)

    if (!layerGroup) return

    const updatedLayers = layerGroup.layers.map((layer) => {
      if (layer.display_name === selectedLayer.display_name) {
        return {
          ...layer,
          visible: !layer.visible,
        }
      }
      return { ...layer }
    })

    handleUpdateLayers(updatedLayers, layerType)
  }

  const handleCancelClick = () => {
    onClose()
  }

  const handleDoneClick = useCallback(() => {
    const stagedHazardAssetTags: string[] = stagedLayers
      .filter((stagedLayer) => stagedLayer.id == 'hazard')
      .flatMap((hazardModalLayer) => hazardModalLayer.layers)
      .filter((hazardLayer) => hazardLayer.hazard_id)
      .map((hazardLayerWithHazardId) => hazardLayerWithHazardId.assetTag)

    const currentHazardLayers = layers.filter((bar) => bar.layerType == 'hazard')

    const deletedHazardIds = currentHazardLayers
      .filter(
        (currentHazard) =>
          currentHazard.hazard_id && !stagedHazardAssetTags.includes(currentHazard.assetTag),
      )
      .map((deletedHazardLayer) => deletedHazardLayer.hazard_id ?? '')

    deletedHazardIds.forEach((deletedHazardId) => {
      setHazardOptionState((prevState: HazardOptionStateByHazard) => {
        const newState = { ...prevState }
        delete newState[deletedHazardId]
        return newState
      })
    })

    const allLayers = stagedLayers.reduce((acc, layer) => {
      const updatedLayers = layer.layers.map((layer) => {
        if (layer.id) return layer

        const generateLayerId = layer.tilesets.map((tileset) => tileset.id).join('-')
        return {
          ...layer,
          id: generateLayerId,
        }
      })

      return [...acc, ...updatedLayers]
    }, [] as MapLayer[])
    prepareAndUpdateLayers({
      layers: allLayers,
      activeHazardParameters: 'onlyslr',
      applyVulnerabilityData: !Boolean(liveData),
    })
    onClose()

    // for all the live layers, add the layers to the active layers
    const selectedLiveLayers =
      (stagedLayers.find((layer) => layer.id === 'live')?.layers as LiveLayer[]) || []
    if (liveMapSelected) {
      setActiveLiveLayers(selectedLiveLayers)
    }
  }, [
    stagedLayers,
    layers,
    setHazardOptionState,
    prepareAndUpdateLayers,
    liveData,
    onClose,
    liveMapSelected,
    setActiveLiveLayers,
  ])

  const handleRemoveLayer = (layer: MapLayer, type: MapLayerType) => {
    const updatedLayers = stagedLayers.map((layerGroup) => {
      if (layerGroup.id === type) {
        return {
          ...layerGroup,
          layers: layerGroup.layers.filter((l) => {
            // for hazards
            if (type == 'hazard') {
              if (layer.hazard_id === undefined && l.hazard_id === undefined)
                return l.assetTag !== layer.assetTag

              return l.hazard_id !== layer.hazard_id
            }

            if (layer.id) return l.id !== layer.id
            // for assets
            if (!layer.assetTag && layer.type) return l.type !== layer.type
            return false
          }),
        }
      }
      return { ...layerGroup }
    })
    setSelectedLive(
      (updatedLayers.find((layer) => layer.id === 'live')?.layers as LiveLayer[]) || [],
    )
    handleUpdateLayers(updatedLayers.flatMap((uLayer) => uLayer.layers))
  }

  useEffect(() => {
    setStagedLayers(mapLayerToModalLayerShape(layers))
  }, [layers])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'Enter') {
        handleDoneClick()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleDoneClick])

  return (
    <Box css={addLayersDialogContainer({ theme })} id="add-layers-dialog">
      <Box display="flex" flexDirection="row" alignItems="center">
        <Typography variant="subtitle2" css={addLayersDialogTitle}>
          Add layers
        </Typography>
        <Box maxWidth={0} position="relative">
          <Button
            onClick={(e) => {
              e.stopPropagation()
              navigate(liveMapSelected ? `/app/live/workspaces/saved` : appLink.riskWorkspaces.path)
              onClose()
            }}
            variant="text"
            size="small"
            css={css`
              align-self: flex-end;
              margin-left: 8px;
              position: absolute;
              right: 0;
              top: 0;
              transform: translateY(-50%);
              color: ${theme.palette.blue.main};
            `}
          >
            Load Workspace
          </Button>
        </Box>
      </Box>
      <Box css={addLayersSetupContainer({ theme })}>
        {!dialogData ? (
          <Box css={loadingSection({ theme })}>
            <CircularProgress color="primary" size="60px" />
          </Box>
        ) : (
          <LayerSetup
            dialogData={dialogData}
            selectedHazards={selectedHazards}
            selectedAssets={selectedAssets}
            selectedInformation={selectedInformation}
            selectedLive={selectedLive}
            onSelectHazardLayer={handleSelectHazardLayer}
            onSelectAssets={handleSelectAssetLayer}
            onSelectInformationLayer={handleSelectInformationLayer}
            onSelectLiveLayer={handleSelectLiveLayer}
            onRemoveLayer={handleRemoveLayer}
            liveData={liveData}
          />
        )}

        <LayersPreview
          isDialogPreview
          dialogProps={{
            stagedLayers,
            handleUpdateLayers,
            handleToggleLayerVisibility,
          }}
        />
      </Box>

      <Box css={addLayersFooter({ theme })}>
        <Button onClick={handleCancelClick} variant="outlined" color="grey">
          Cancel
        </Button>
        <Button onClick={handleDoneClick}>Done</Button>
      </Box>
    </Box>
  )
}
